import { WEED_STAGES, POT_WATER_MAX_ML, POT_WATER_PER_USE_ML } from "@ghostland/shared";
import { assetUrl } from "../assetVer.js";
import { injectTheme } from "./theme.js";

/**
 * The weed-plant status panel (E near a plant): LEFT = the current stage's pixel art; RIGHT = the
 * GROWTH and WATER bars (+ a rot countdown while thirsty); BOTTOM = the context action — Water a
 * thirsty plant, Harvest a ready one, Clear a rotten one. Live: world3d re-renders it from the
 * synced WeedPlant every server tick and closes it when the player walks away (like the chest).
 */

export interface PlantPanelData {
  stage: number;
  status: string; // thirsty | growing | ready | rotten
  growth: number; // 0..100
  water: number;  // 0..100 (current stage's remaining water)
  dryLeftMs: number;
}

export interface PlantPanelActions {
  water: (plantId: string) => void;
  harvest: (plantId: string) => void;
  clear: (plantId: string) => void;
  /** Water (ml) in the player's fullest carried pot (drives the Water button label/state). */
  potMl: () => number;
}

const STYLE_ID = "ghostland-plant-style";
const CSS = `
#gl-plant-overlay {
  position: fixed; left: 50%; bottom: 96px; transform: translateX(-50%); z-index: 28;
  display: none; font-family: ui-monospace, "SFMono-Regular", Menlo, monospace; color: var(--gl-text);
}
#gl-plant-overlay.open { display: block; }
#gl-plant-card {
  min-width: 320px; padding: 12px 14px 12px; background: var(--gl-panel);
  border: 1px solid var(--gl-border); border-radius: 12px;
  box-shadow: 0 16px 50px rgba(0,0,0,.7), 0 0 0 1px rgba(116,216,106,.15);
}
#gl-plant-head { display: flex; align-items: baseline; justify-content: space-between; gap: 18px; margin: 0 0 10px; }
#gl-plant-head h1 { margin: 0; font-size: 13px; letter-spacing: .16em; }
#gl-plant-head .hint { font-size: 10px; color: var(--gl-muted); }
#gl-plant-body { display: flex; gap: 14px; align-items: center; }
#gl-plant-art {
  width: 96px; height: 96px; flex: none; display: flex; align-items: flex-end; justify-content: center;
  background: var(--gl-panel-2); border: 2px solid var(--gl-border); border-radius: 8px; overflow: hidden;
}
#gl-plant-art img { image-rendering: pixelated; max-width: 88px; max-height: 88px; }
#gl-plant-bars { flex: 1; display: flex; flex-direction: column; gap: 8px; min-width: 170px; }
.gl-plant-row label { display: block; font-size: 10px; letter-spacing: 1px; color: var(--gl-muted); margin-bottom: 3px; }
.gl-plant-bar { height: 10px; border-radius: 5px; background: rgba(255,255,255,.07); border: 1px solid var(--gl-border); overflow: hidden; }
.gl-plant-bar > div { height: 100%; border-radius: 4px; transition: width .4s linear; }
#gl-plant-growfill { background: linear-gradient(90deg, #74d86a, #a8f09a); }
#gl-plant-waterfill { background: linear-gradient(90deg, #3f9fd8, #86d4f4); }
#gl-plant-note { font-size: 10px; color: var(--gl-muted); }
#gl-plant-note b { color: #f2f2f2; }
#gl-plant-note .warn { color: #ff7a6a; font-weight: 700; }
#gl-plant-foot { margin-top: 10px; display: flex; justify-content: center; }
#gl-plant-btn {
  min-width: 160px; padding: 8px 16px; border-radius: 8px; cursor: pointer;
  font-family: inherit; font-size: 12px; font-weight: 700; letter-spacing: .08em;
  background: var(--gl-panel-2); color: var(--gl-text); border: 2px solid var(--gl-border);
}
#gl-plant-btn:hover:not(:disabled) { border-color: #74d86a; box-shadow: 0 0 0 2px rgba(116,216,106,.25) inset; }
#gl-plant-btn.harvest { border-color: #74d86a; color: #a8f09a; }
#gl-plant-btn:disabled { opacity: .45; cursor: default; }
`;

function ensureStyle(): void {
  injectTheme();
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style"); s.id = STYLE_ID; s.textContent = CSS;
  document.head.appendChild(s);
}

let overlay: HTMLDivElement | null = null;
let plantId = "";
let actions: PlantPanelActions | null = null;

function ensureOverlay(): HTMLDivElement {
  if (overlay) return overlay;
  ensureStyle();
  overlay = document.createElement("div");
  overlay.id = "gl-plant-overlay";
  overlay.innerHTML = `
    <div id="gl-plant-card">
      <div id="gl-plant-head"><h1>WEED PLANT</h1><span class="hint" id="gl-plant-stage"></span></div>
      <div id="gl-plant-body">
        <div id="gl-plant-art"><img alt="" /></div>
        <div id="gl-plant-bars">
          <div class="gl-plant-row"><label>GROWTH <span id="gl-plant-growpct"></span></label>
            <div class="gl-plant-bar"><div id="gl-plant-growfill"></div></div></div>
          <div class="gl-plant-row"><label>WATER <span id="gl-plant-waterpct"></span></label>
            <div class="gl-plant-bar"><div id="gl-plant-waterfill"></div></div></div>
          <div id="gl-plant-note"></div>
        </div>
      </div>
      <div id="gl-plant-foot"><button id="gl-plant-btn"></button></div>
    </div>`;
  document.body.appendChild(overlay);
  overlay.querySelector<HTMLButtonElement>("#gl-plant-btn")!.addEventListener("click", () => {
    if (!plantId || !actions) return;
    const kind = overlay!.querySelector<HTMLButtonElement>("#gl-plant-btn")!.dataset.kind;
    if (kind === "water") actions.water(plantId);
    else if (kind === "harvest") actions.harvest(plantId);
    else if (kind === "clear") actions.clear(plantId);
  });
  return overlay;
}

function fmtMs(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

function render(data: PlantPanelData): void {
  if (!overlay) return;
  const q = <T extends HTMLElement>(sel: string): T => overlay!.querySelector<T>(sel)!;
  const rotten = data.status === "rotten";
  const stage = Math.max(1, Math.min(WEED_STAGES, data.stage));
  q<HTMLSpanElement>("#gl-plant-stage").textContent = rotten
    ? "ROTTEN"
    : data.status === "ready" ? "READY TO HARVEST" : `STAGE ${stage}/${WEED_STAGES}`;
  q<HTMLImageElement>("#gl-plant-art img").src =
    assetUrl(`/assets/scene/kapon/${rotten ? "weed_rotten" : `weed_stage${stage}`}.png`);
  q<HTMLDivElement>("#gl-plant-growfill").style.width = `${rotten ? 0 : data.growth}%`;
  q<HTMLSpanElement>("#gl-plant-growpct").textContent = rotten ? "—" : `${data.growth}%`;
  q<HTMLDivElement>("#gl-plant-waterfill").style.width = `${rotten ? 0 : data.water}%`;
  q<HTMLSpanElement>("#gl-plant-waterpct").textContent = rotten ? "—" : `${data.water}%`;

  const note = q<HTMLDivElement>("#gl-plant-note");
  if (rotten) note.innerHTML = `<span class="warn">It withered without water.</span>`;
  else if (data.status === "ready") note.innerHTML = `Heavy with buds — harvest it!`;
  else if (data.status === "thirsty") note.innerHTML = `<span class="warn">Thirsty — dies in ${fmtMs(data.dryLeftMs)}.</span>`;
  else note.innerHTML = `Growing steadily.`;

  const btn = q<HTMLButtonElement>("#gl-plant-btn");
  btn.classList.toggle("harvest", data.status === "ready");
  if (data.status === "ready") {
    btn.dataset.kind = "harvest"; btn.disabled = false; btn.textContent = "HARVEST";
  } else if (rotten) {
    btn.dataset.kind = "clear"; btn.disabled = false; btn.textContent = "CLEAR";
  } else if (data.status === "thirsty") {
    const ml = actions?.potMl() ?? 0;
    btn.dataset.kind = "water";
    btn.disabled = ml < POT_WATER_PER_USE_ML;
    btn.textContent = ml < POT_WATER_PER_USE_ML ? "POT EMPTY" : `WATER (${ml}/${POT_WATER_MAX_ML}ml)`;
  } else {
    btn.dataset.kind = ""; btn.disabled = true; btn.textContent = "GROWING…";
  }
}

/** Open the panel for a plant. `update` keeps it live; it closes on walk-away/Esc (world3d). */
export function openPlantPanel(id: string, data: PlantPanelData, a: PlantPanelActions): void {
  ensureOverlay();
  plantId = id; actions = a;
  render(data);
  overlay!.classList.add("open");
}

/** Refresh the open panel (synced plant changed / inventory changed); ignored for other plants. */
export function updatePlantPanel(id: string, data: PlantPanelData): void {
  if (!isPlantPanelOpen() || id !== plantId) return;
  render(data);
}

export function closePlantPanel(): void {
  overlay?.classList.remove("open");
  plantId = ""; actions = null;
}

export function isPlantPanelOpen(): boolean {
  return overlay?.classList.contains("open") ?? false;
}

export function currentPlantId(): string {
  return isPlantPanelOpen() ? plantId : "";
}
